import { useMemo } from 'react';
import type { BracketMatch, HistoryResponse, HistorySeason } from '../../shared/types';
import { Avatar, Empty, PageHead, Section, medalClass } from '../components/common';
import { efficiency, fmtPct, fmtPoints, fmtRecord, ordinal } from '../lib/format';

/**
 * Human round names, derived from the bracket's shape rather than hard-coded,
 * since league size and playoff-team count both vary.
 *
 * `advancingMatches` disambiguates the third-from-last round: with 8 playoff
 * teams it holds 4 real quarterfinals, but with 6 teams (this league) the top
 * two seeds have byes and it is a 2-game wild-card round.
 */
function roundLabel(round: number, maxRound: number, advancingMatches: number): string {
  const fromEnd = maxRound - round;
  if (fromEnd === 0) return 'Finals';
  if (fromEnd === 1) return 'Semifinals';
  if (fromEnd === 2) return advancingMatches >= 4 ? 'Quarterfinals' : 'Wild Card';
  return `Round ${round}`;
}

function placementLabel(m: BracketMatch, playoffTeams: number): string | null {
  if (m.placement == null) return null;
  const base = m.bracket === 'losers' ? playoffTeams + m.placement : m.placement;
  if (base === 1) return 'Championship';
  return `${ordinal(base)} place`;
}

function Bracket({ season }: { season: HistorySeason }) {
  const winners = season.bracket.filter((m) => m.bracket === 'winners');
  if (winners.length === 0) return <Empty>No playoff bracket recorded.</Empty>;

  const names = new Map(season.placements.map((p) => [p.roster_id, p]));
  const maxRound = Math.max(...winners.map((m) => m.round));
  const rounds = [...new Set(winners.map((m) => m.round))].sort((a, b) => a - b);

  const teamName = (id: number | null) => {
    if (id == null) return 'TBD';
    const p = names.get(id);
    return p ? (p.team_name ?? p.display_name) : `Roster ${id}`;
  };

  return (
    <div className="bracket">
      {rounds.map((r) => (
        <div className="bracket-round" key={r}>
          <h4>
            {roundLabel(
              r,
              maxRound,
              winners.filter((m) => m.round === r && m.placement == null).length,
            )}
          </h4>
          {winners
            .filter((m) => m.round === r)
            // Advancement games first, then placement games in finishing order.
            // Using `?? 99` instead would float the 5th-place game above the
            // semifinals that feed the championship.
            .sort((a, b) => (a.placement ?? 0) - (b.placement ?? 0) || a.match_id - b.match_id)
            .map((m) => {
              const label = placementLabel(m, season.playoff_teams);
              return (
                <div className="match" key={m.match_id}>
                  {label && <div className="match-label">{label}</div>}
                  {[m.team1, m.team2].map((t, i) => (
                    <div
                      className={`match-team ${t != null && t === m.winner ? 'won' : ''}`}
                      key={i}
                    >
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {teamName(t)}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })}
        </div>
      ))}
    </div>
  );
}

function SeasonBlock({ season }: { season: HistorySeason }) {
  const champ = season.placements[0];

  return (
    <Section title={`${season.season} Season`} hint={`${season.placements.length} teams`}>
      {champ && (
        <div className="card champ" style={{ marginBottom: 16 }}>
          <div className="champ-body">
            <Avatar id={champ.avatar} alt={champ.team_name ?? champ.display_name} large />
            <div className="champ-id">
              <div className="eyebrow">Champion</div>
              <div className="champ-name">{champ.team_name ?? champ.display_name}</div>
              <div className="champ-manager">@{champ.display_name}</div>
            </div>
            {/* Same trailing numbers as the home page's champion card. This one
                spans the full content width, so without them the card is a name
                against roughly 800px of nothing. */}
            <dl className="champ-line">
              <div>
                <dt>Regular season</dt>
                <dd>{fmtRecord(champ.record)}</dd>
              </div>
              <div>
                <dt>Points for</dt>
                <dd>{fmtPoints(champ.record.pointsFor)}</dd>
              </div>
            </dl>
          </div>
        </div>
      )}

      <div className="table-scroll" style={{ marginBottom: 18 }}>
        <table>
          <thead>
            <tr>
              <th className="rank">#</th>
              <th>Team</th>
              <th className="num">Record</th>
              <th className="num">PF</th>
              <th className="num">PA</th>
              <th className="num">MAX</th>
              <th className="num">EFF</th>
            </tr>
          </thead>
          <tbody>
            {season.placements.map((p) => {
              const eff = efficiency(p.record);
              return (
                <tr key={p.roster_id}>
                  <td className={`rank ${medalClass(p.place)}`}>{p.place}</td>
                  <td>
                    <div className="team-cell">
                      <Avatar id={p.avatar} alt={p.team_name ?? p.display_name} />
                      <div className="team-names">
                        <div className="team-title">{p.team_name ?? p.display_name}</div>
                        <div className="team-handle">@{p.display_name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="num">{fmtRecord(p.record)}</td>
                  <td className="num">{fmtPoints(p.record.pointsFor)}</td>
                  <td className="num faint">{fmtPoints(p.record.pointsAgainst)}</td>
                  <td className="num faint">
                    {p.record.potentialPoints ? fmtPoints(p.record.potentialPoints) : '—'}
                  </td>
                  <td className="num">{eff == null ? '—' : fmtPct(eff)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <h4
        style={{
          fontSize: 12,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: 'var(--text-faint)',
          fontFamily: 'var(--body)',
          marginBottom: 10,
        }}
      >
        Playoff Bracket
      </h4>
      <Bracket season={season} />
    </Section>
  );
}

/** All-time ledger. Thin after one season, but it accrues every year. */
function AllTime({ history }: { history: HistoryResponse }) {
  const rows = useMemo(() => {
    const agg = new Map<
      string,
      {
        name: string;
        avatar: string | null;
        seasons: number;
        titles: number;
        finals: number;
        playoffs: number;
        wins: number;
        losses: number;
        ties: number;
        pf: number;
        best: number;
      }
    >();

    for (const season of history.seasons) {
      for (const p of season.placements) {
        const key = p.owner_id ?? `roster-${season.season}-${p.roster_id}`;
        const cur = agg.get(key) ?? {
          name: p.display_name,
          avatar: p.avatar,
          seasons: 0,
          titles: 0,
          finals: 0,
          playoffs: 0,
          wins: 0,
          losses: 0,
          ties: 0,
          pf: 0,
          best: Number.POSITIVE_INFINITY,
        };
        cur.seasons += 1;
        if (p.place === 1) cur.titles += 1;
        if (p.place <= 2) cur.finals += 1;
        if (p.place <= season.playoff_teams) cur.playoffs += 1;
        cur.wins += p.record.wins;
        cur.losses += p.record.losses;
        cur.ties += p.record.ties;
        cur.pf += p.record.pointsFor;
        cur.best = Math.min(cur.best, p.place);
        cur.avatar = p.avatar ?? cur.avatar;
        agg.set(key, cur);
      }
    }

    return [...agg.values()].sort(
      (a, b) => b.titles - a.titles || b.wins - a.wins || b.pf - a.pf,
    );
  }, [history]);

  return (
    <Section title="All-Time Ledger">
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Manager</th>
              <th className="num">Seasons</th>
              <th className="num">Titles</th>
              <th className="num">Finals</th>
              <th className="num">Playoffs</th>
              <th className="num">Record</th>
              <th className="num">Total PF</th>
              <th className="num">Best</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.name}>
                <td>
                  <div className="team-cell">
                    <Avatar id={r.avatar} alt={r.name} />
                    <div className="team-title">@{r.name}</div>
                  </div>
                </td>
                <td className="num faint">{r.seasons}</td>
                <td className="num" style={{ color: r.titles ? 'var(--gold)' : undefined }}>
                  {r.titles || '—'}
                </td>
                <td className="num faint">{r.finals || '—'}</td>
                <td className="num faint">{r.playoffs || '—'}</td>
                <td className="num">
                  {r.wins}-{r.losses}
                  {r.ties ? `-${r.ties}` : ''}
                </td>
                <td className="num">{fmtPoints(r.pf)}</td>
                <td className={`num ${medalClass(r.best)}`}>{ordinal(r.best)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

export default function History({ history }: { history: HistoryResponse }) {
  if (history.seasons.length === 0) {
    return (
      <>
        <PageHead title="History" />
        <div className="card">
          <Empty>No completed seasons yet.</Empty>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHead
        title="History"
        meta={[
          history.seasons.length === 1
            ? 'One completed season'
            : `${history.seasons.length} completed seasons`,
          'Final standings and playoff brackets',
        ]}
      />

      <AllTime history={history} />

      {history.seasons.map((s) => (
        <SeasonBlock season={s} key={s.league_id} />
      ))}
    </>
  );
}
